import { useEffect, useState, useRef, Suspense, lazy } from "react";
import { Expand, Loader2 } from "lucide-react";
import { Highlight, themes } from "prism-react-renderer";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { CopyButton } from "~/components/assistant/CopyButton";
import { ArtifactToolbarActions } from "~/components/assistant/messages/ArtifactToolbarActions";
import { DetailField } from "~/components/assistant/messages/DetailField";
import { FullscreenArtifactModal } from "~/components/assistant/messages/FullscreenArtifactModal";
import { LineageTab } from "~/components/assistant/messages/LineageTab";
import { DownloadDialog } from "~/components/assistant/messages/download/DownloadDialog";
import { ExportDialog } from "~/components/assistant/messages/export/ExportDialog";
import { Figure } from "~/components/assistant/types";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/monitoring";

// Lazy load the Plot component
const PlotComponent = lazy(() => import("react-plotly.js"));
const MIN_INLINE_ARTIFACT_WIDTH = 400;

interface FigureMessageProps {
  figure: Figure;
  debugMode?: boolean;
}

type TabType = "chart" | "code" | "lineage" | "details";

interface Tab {
  id: TabType;
  label: string;
}

// Simple Tabs component for figure display
export function FigureMessage({ figure, debugMode = false }: FigureMessageProps) {
  const [activeTab, setActiveTab] = useState<TabType>("chart");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [isCompactArtifactView, setIsCompactArtifactView] = useState(false);
  const messageRef = useRef<HTMLDivElement>(null);
  const { conversationId, contextId } = useAssistantContext();

  // Load Python language support for syntax highlighting
  const { isLoading, Prism: PrismInstance } = usePrismLanguages(["python"]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);

    trackEvent("analysis_agent_artifact_tab_viewed", {
      dataset_id: contextId || "Unknown",
      conversation_id: conversationId || undefined,
      artifact_id: figure.artifact_id || undefined,
      artifact_type: "figure",
      context: "dataset",
      tab,
    });
  };

  const tabs: Tab[] = [{ id: "chart", label: "Chart" }];
  if (figure.code) {
    tabs.push({ id: "code", label: "Code" });
  }

  if (figure?.artifact_id) {
    tabs.push({ id: "lineage", label: "Lineage" });
  }

  if (debugMode) {
    tabs.push({ id: "details", label: "Details" });
  }

  const chartTitle =
    typeof figure.content.layout?.title === "string"
      ? figure.content.layout.title
      : (figure.content.layout?.title as { text?: string })?.text || "Chart";

  useEffect(() => {
    const element = messageRef.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) {
        return;
      }
      setIsCompactArtifactView(entry.contentRect.width <= MIN_INLINE_ARTIFACT_WIDTH);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleOpenFullscreen = (source: "toolbar" | "compact-placeholder") => {
    setIsFullscreen(true);
    trackEvent("analysis_agent_artifact_fullscreen_opened", {
      dataset_id: contextId || "Unknown",
      conversation_id: conversationId || undefined,
      artifact_id: figure.artifact_id || undefined,
      artifact_type: "figure",
      source,
    });
  };

  const renderTabButtons = () =>
    tabs.map((tab) => (
      <button
        key={tab.id}
        onClick={() => handleTabChange(tab.id)}
        className={cn(
          "px-3 py-2 text-xs font-medium border-b-2 transition-colors select-none cursor-pointer",
          "max-w-20 min-w-0 overflow-hidden whitespace-nowrap text-ellipsis break-normal",
          activeTab === tab.id
            ? "border-gray-900 text-gray-900"
            : "border-transparent text-gray-600 hover:text-gray-900"
        )}
      >
        {tab.label}
      </button>
    ));

  const renderPlot = (fullscreen: boolean) => (
    <Suspense
      fallback={
        <div className="flex items-center justify-center h-96 text-gray-500">
          <div className="text-center">
            <Loader2 className="size-8 text-gray-400 animate-spin mx-auto mb-2" />
            <p className="text-sm">Loading chart...</p>
          </div>
        </div>
      }
    >
      <PlotComponent
        data={figure.content.data}
        layout={{
          ...figure.content.layout,
          // Let Plotly handle dimensions with the container size
          width: undefined,
          height: undefined,
          autosize: true,
          // Ensure proper margins for responsive layout
          margin: {
            l: figure.content.layout.margin?.l || 50,
            r: figure.content.layout.margin?.r || 50,
            t: figure.content.layout.margin?.t || 50,
            b: figure.content.layout.margin?.b || 50,
          },
        }}
        config={{
          displaylogo: false,
          responsive: true,
          displayModeBar: "hover",
          modeBarButtons: [
            ["zoomIn2d", "zoomOut2d", "pan2d", "resetScale2d"],
            ["hoverClosestCartesian", "hoverCompareCartesian"],
            ["toImage"],
          ],
        }}
        useResizeHandler={true}
        className="w-full h-full"
        key={fullscreen ? `plot-fs-${figure.artifact_id}` : `plot-${figure.artifact_id}`}
        divId={fullscreen ? `plot-fs-${figure.artifact_id}` : `plot-${figure.artifact_id}`}
      />
    </Suspense>
  );

  const renderCodeContent = () => {
    if (activeTab !== "code" || !figure.code) return null;
    return (
      <>
        {/* Code Header with Copy Button */}
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-medium text-gray-700">Python Code</h4>
          <CopyButton text={figure.code} variant="light" size="sm" />
        </div>
        <div className="h-full overflow-hidden rounded-sm">
          {!isLoading && (
            <Highlight
              theme={themes.dracula}
              code={figure.code}
              language="python"
              prism={PrismInstance as any}
            >
              {({ style, tokens, getLineProps, getTokenProps, className }) => (
                <pre
                  className={cn(
                    "not-prose h-full p-4 overflow-x-auto text-xs font-mono leading-relaxed",
                    className
                  )}
                  style={style}
                >
                  {tokens.map((line, i) => (
                    <div
                      {...getLineProps({ line, key: i })}
                      key={`line-${i}`}
                      style={{ minHeight: "1.5em" }}
                    >
                      {line.map((token, key) => (
                        <span {...getTokenProps({ token, key })} key={`token-${key}`} />
                      ))}
                    </div>
                  ))}
                </pre>
              )}
            </Highlight>
          )}
          {isLoading && (
            <div className="h-full p-4 bg-gray-800 text-gray-300 text-sm font-mono leading-relaxed">
              Loading syntax highlighting...
            </div>
          )}
        </div>
      </>
    );
  };

  const renderLineageContent = () => {
    if (activeTab !== "lineage" || !figure?.artifact_id) return null;
    return <LineageTab artifactId={figure.artifact_id} />;
  };

  return (
    <>
      <div ref={messageRef} className="bg-white border border-gray-200 rounded-lg p-4 pt-1 w-full">
        {/* Tabs */}
        <div className="flex items-center border-b border-gray-200 mb-4 overflow-hidden">
          {renderTabButtons()}
          <ArtifactToolbarActions
            onDownload={
              figure.artifact_id
                ? () => {
                    setShowDownloadDialog(true);
                    trackEvent("analysis_agent_artifact_download_requested", {
                      dataset_id: contextId || "Unknown",
                      conversation_id: conversationId || undefined,
                      artifact_id: figure.artifact_id || undefined,
                      artifact_type: "figure",
                    });
                  }
                : undefined
            }
            onExport={() => {
              setShowExportDialog(true);
              trackEvent("analysis_agent_artifact_export_opened", {
                dataset_id: contextId || "Unknown",
                conversation_id: conversationId || undefined,
                artifact_id: figure.artifact_id || undefined,
                artifact_type: "figure",
              });
            }}
            onFullscreen={() => handleOpenFullscreen("toolbar")}
          />
        </div>

        {/* Tab Content */}
        <div className="overflow-x-auto">
          {activeTab === "chart" && (
            <div className="bg-white rounded-sm">
              {isCompactArtifactView ? (
                <div className="flex min-h-48 items-center justify-center rounded-md px-4 py-5">
                  <div className="flex max-w-xs flex-col items-center gap-3 text-center">
                    <p className="text-xs text-gray-600">
                      This chart is easier to read in fullscreen.
                    </p>
                    <button
                      type="button"
                      onClick={() => handleOpenFullscreen("compact-placeholder")}
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 transition-colors hover:bg-gray-100 cursor-pointer"
                    >
                      <Expand className="size-3.5" />
                      View this chart
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-full overflow-hidden" style={{ height: "450px" }}>
                    {renderPlot(false)}
                  </div>
                  {figure?.summary && (
                    <div className="mt-3 text-xs text-gray-600 px-2">{figure.summary}</div>
                  )}
                </>
              )}
            </div>
          )}
          {renderCodeContent()}
          {renderLineageContent()}
          {activeTab === "details" && debugMode && (
            <div className="space-y-3 pt-2 pb-4 px-4 rounded-lg border border-gray-200 text-gray-800">
              <DetailField label="Figure Data" content={figure} />
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Modal */}
      <FullscreenArtifactModal
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        title={chartTitle}
      >
        {/* Tabs (no fullscreen button in modal) */}
        <div className="flex border-b border-gray-200 mb-4 shrink-0">{renderTabButtons()}</div>

        {/* Content fills remaining space */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {activeTab === "chart" && (
            <div className="h-full flex flex-col">
              <div className="w-full flex-1 min-h-0 overflow-hidden">{renderPlot(true)}</div>
              {figure?.summary && (
                <div className="shrink-0 mt-3 text-xs text-gray-600 px-2">{figure.summary}</div>
              )}
            </div>
          )}
          {activeTab === "code" && figure.code && (
            <div className="h-full overflow-auto">{renderCodeContent()}</div>
          )}
          {activeTab === "lineage" && figure?.artifact_id && (
            <div className="h-full overflow-auto">{renderLineageContent()}</div>
          )}
          {activeTab === "details" && debugMode && (
            <div className="h-full overflow-auto">
              <div className="space-y-3 pt-2 pb-4 px-4 rounded-lg border border-gray-200 text-gray-800">
                <DetailField label="Figure Data" content={figure} />
              </div>
            </div>
          )}
        </div>
      </FullscreenArtifactModal>

      {/* Export Dialog */}
      <ExportDialog
        open={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        artifact={figure}
        artifactType="figure"
        conversationId={conversationId || ""}
      />

      {/* Download Dialog */}
      <DownloadDialog
        open={showDownloadDialog}
        onClose={() => setShowDownloadDialog(false)}
        artifact={figure}
        artifactType="figure"
      />
    </>
  );
}
